/**
 * Conversion rates between a ledger currency and another currency, as scaled
 * bigints. The one copy of this arithmetic: svc-finance credits and pays out
 * with it, the backoffice previews with it, and the player app shows balances
 * with it, so the figure a screen quotes and the figure the server moves can
 * only disagree if the inputs do.
 *
 * A rate is `units of the OTHER currency per ONE unit of the LEDGER currency`:
 * `'700'` for a LAK account under a THB brand means 700 LAK = 1 THB. The inverse
 * reading of the same number is wrong by a factor of 490,000, so the direction is
 * fixed everywhere.
 *
 * Rates travel as decimal strings with at most `RATE_SCALE` fractional digits.
 * A seventh digit is refused, never truncated: filing 0.1234567 as 0.123456
 * would convert every transfer by a rate nobody entered.
 *
 * Pure and dependency-free apart from `./money.ts`, so a browser bundle can take
 * this subpath without dragging the service barrel in.
 */
import { multiplier } from './money.js';

export const RATE_SCALE = 6;
export const RATE_FACTOR = 10n ** BigInt(RATE_SCALE);

/** Digits before the point are bounded so an absurd paste cannot become a bigint of any size. */
const RATE_RE = /^(\d{1,14})(?:\.(\d+))?$/;

/**
 * `'712.5'` → `712500000n`. `null` for blank, zero, negative, signed, exponent,
 * malformed, or more than `RATE_SCALE` fractional digits.
 */
export function parseRate(rate: string | null | undefined): bigint | null {
  if (rate == null) return null;
  const m = RATE_RE.exec(rate.trim());
  if (!m) return null;
  const fraction = m[2] ?? '';
  if (fraction.length > RATE_SCALE) return null;
  const scaled = BigInt(m[1]!) * RATE_FACTOR + BigInt(fraction.padEnd(RATE_SCALE, '0'));
  return scaled > 0n ? scaled : null;
}

/** Canonical text form: `700000000n` → `'700'`, `712500000n` → `'712.5'`. */
export function formatRate(scaled: bigint): string {
  const whole = scaled / RATE_FACTOR;
  const frac = (scaled % RATE_FACTOR).toString().padStart(RATE_SCALE, '0').replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole.toString();
}

/** Same number, same text: `'700.000'` and `'700'` compare equal. Two unusable rates never do. */
export function sameRate(a: string | null | undefined, b: string | null | undefined): boolean {
  const pa = parseRate(a);
  return pa !== null && pa === parseRate(b);
}

/**
 * Minor units of `otherCurrency` → minor units of `ledgerCurrency` at `scaled`,
 * truncated toward zero: the fraction of a ledger minor unit belongs to the
 * platform, never to the player (CLAUDE.md §2.5).
 *
 *   1,000,000 LAK at 700 → 142857 (฿1,428.57)
 */
export function toLedgerMinor(
  amount: bigint,
  otherCurrency: string,
  ledgerCurrency: string,
  scaled: bigint,
): bigint {
  return (amount * multiplier(ledgerCurrency) * RATE_FACTOR) / (scaled * multiplier(otherCurrency));
}

/**
 * Minor units of `ledgerCurrency` → minor units of `otherCurrency` at `scaled`,
 * truncated toward zero: a payout gets the whole minor units the rate yields and
 * the fraction stays with the platform (§2.5).
 *
 *   ฿2,800.00 at 700 → 1,960,000 LAK
 */
export function fromLedgerMinor(
  ledgerAmount: bigint,
  ledgerCurrency: string,
  otherCurrency: string,
  scaled: bigint,
): bigint {
  return (ledgerAmount * scaled * multiplier(otherCurrency)) / (RATE_FACTOR * multiplier(ledgerCurrency));
}
